import { Injectable, signal, computed, inject } from '@angular/core';
import { FaviconService } from './favicon.service';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'taskeo.theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly faviconService = inject(FaviconService);
  private readonly themeSignal = signal<Theme>('light');
  private mediaQueryList: MediaQueryList | null = null;
  private mediaListener: (() => void) | null = null;

  readonly theme = this.themeSignal.asReadonly();
  readonly isDark = computed(() => this.themeSignal() === 'dark');

  get isFollowingSystem(): boolean {
    if (typeof window === 'undefined') return false;
    const value = localStorage.getItem(STORAGE_KEY);
    return value !== 'light' && value !== 'dark';
  }

  initTheme(): void {
    const storedTheme = this.readStored();
    this.themeSignal.set(storedTheme);
    this.applyToDocument(storedTheme);
    setTimeout(() => this.faviconService.setTheme(storedTheme), 0);

    // Only enable transitions after first paint to avoid initial flicker.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.classList.add('theme-ready');
      });
    });

    this.subscribeToSystemTheme();
  }

  setTheme(theme: Theme): void {
    this.themeSignal.set(theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {}
    this.applyToDocument(theme);
    this.faviconService.setTheme(theme);
  }

  useSystemTheme(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('taskeo-theme');
    } catch {}
    const theme = this.getSystemTheme();
    this.themeSignal.set(theme);
    this.applyToDocument(theme);
    this.faviconService.setTheme(theme);
  }

  toggleTheme(): void {
    const next = this.themeSignal() === 'dark' ? 'light' : 'dark';
    this.setTheme(next);
  }

  private getSystemTheme(): Theme {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private readStored(): Theme {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      if (value === 'dark' || value === 'light') return value;
      return this.getSystemTheme();
    } catch {
      return this.getSystemTheme();
    }
  }

  private subscribeToSystemTheme(): void {
    if (typeof window === 'undefined') return;
    this.mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');
    this.mediaListener = () => {
      if (this.isFollowingSystem) {
        const systemTheme = this.getSystemTheme();
        this.themeSignal.set(systemTheme);
        this.applyToDocument(systemTheme);
        this.faviconService.setTheme(systemTheme);
      }
    };
    this.mediaQueryList.addEventListener('change', this.mediaListener);
  }

  private applyToDocument(theme: Theme): void {
    document.documentElement.dataset['theme'] = theme;
    document.documentElement.style.colorScheme = theme;
  }
}
